<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText } from "@chatfood/design-system";
import { BasketIconBadge } from "../basket-icon-badge";
import { computed } from "vue";
import { BasketViewCheckout } from "../basket-view-checkout";
import { BasketMinBanner } from "../basket-min-banner";
import { BasketSummary } from "../basket-summary";
import { BasketClaims } from "../basket-claims";
import { PriceBreakdownToggle } from "../price-breakdown-toggle";
import { useBasketStore } from "~/stores/basket";
import { useLocale } from "~/composables/use-locale";
import { useHeaderHeight } from "~/composables/use-header-height";

const css = bemBuilder("basket-view");
const { t } = useLocale();
const { headerHeightPixel } = useHeaderHeight();

const basket = useBasketStore();

const isEmpty = computed(() => {
  return basket.items.length === 0;
});
</script>

<template>
  <div :class="[css(''), !isEmpty && css('', 'self-scroll')]">
    <div :class="css('title-row')">
      <BasketIconBadge />
      <AtomText :class="css('title')" type="h2">
        {{ t("components.basket_view.title") }}
      </AtomText>
    </div>
    <div v-if="isEmpty" :class="css('empty-basket')">
      <AtomText color="color-text-secondary">
        {{ t("components.basket_view.empty_message") }}
      </AtomText>
    </div>
    <template v-else>
      <div :class="css('content')">
        <div :class="css('block')">
          <BasketSummary />
        </div>
        <div :class="css('basket-claims')">
          <BasketClaims />
        </div>
      </div>
      <div :class="css('footer')">
        <BasketMinBanner />
        <PriceBreakdownToggle />
        <BasketViewCheckout />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
$header-height: v-bind(headerHeightPixel);
$content-padding: calc(var(--spacing-2xl) * 2);

.basket-view {
  display: flex;
  flex-direction: column;

  &__title-row {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-lg);
  }

  &__title {
    padding: 0;
  }

  &__block {
    border-top: 1px solid var(--color-base-95);
  }

  &__basket-claims {
    padding: var(--spacing-lg);
    border-top: 1px solid var(--color-base-95);
  }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding: var(--spacing-lg);
    border-top: 1px solid var(--color-base-95);
    flex-shrink: 0;
  }

  &__empty-basket {
    padding: var(--spacing-2xl) var(--spacing-md);
    text-align: center;
  }

  @media (width >= $breakpoint-lg) {
    &--self-scroll {
      height: calc(100dvh - $header-height - $content-padding);
      overflow: hidden;
    }

    &--self-scroll &__content {
      flex: 1;
      min-height: 30dvh;
      overflow-y: auto;
    }
  }
}
</style>
